import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import type { Alert, AlertSection, DealSummary } from "@edge/shared";
import { Panel } from "../shared/Panel";
import { CrmTaskCreateDialog } from "../shared/CrmTaskCreateDialog";
import { SaveNoteDialog, type SaveNoteInput } from "../shared/SaveNoteDialog";
import { AddToMyListDialog } from "../shared/AddToMyListDialog";
import type { CrmTaskCreateInput } from "../../hooks/useCrmTaskActions";
import type { PersonalTaskInput } from "../../hooks/usePersonalTasks";
import { detectDealFromAlert } from "../../lib/detect-deal";

/**
 * Renderer-side merge of classifier sections per v3.1 spec:
 *   - needs_response    → top:    NEEDS YOUR RESPONSE
 *   - proactive/submission/strategic → middle: PROACTIVE INTELLIGENCE
 *   - awaiting_reply    → bottom: AWAITING REPLY · 48H+
 * Classifier output is unchanged; this is purely how we display.
 */
type MergedSectionKey = "needs_response" | "proactive_intel" | "awaiting_reply";
const MERGED_SECTIONS: ReadonlyArray<{
  key: MergedSectionKey;
  label: string;
  includes: ReadonlyArray<AlertSection>;
}> = [
  { key: "needs_response", label: "NEEDS YOUR RESPONSE", includes: ["needs_response"] },
  {
    key: "proactive_intel",
    label: "PROACTIVE INTELLIGENCE",
    includes: ["proactive", "submission", "strategic"],
  },
  { key: "awaiting_reply", label: "AWAITING REPLY · 48H+", includes: ["awaiting_reply"] },
];

interface AlertsRailProps {
  alerts: Alert[];
  loading?: boolean;
  /** "centerpiece" = gold-bordered home-cockpit shell with refresh + filter pill.
   *  "sidepanel"   = cyan Panel shell used in focus mode (default).
   *  "embedded"    = body only, no shell — for hosting inside the cockpit
   *                  WatchtowerActivityPanel tab (CK-1). */
  variant?: "centerpiece" | "sidepanel" | "embedded";
  /** Triggered by the centerpiece's ↻ REFRESH pill. Local spinner state. */
  onRefresh?: () => Promise<void> | void;
  /**
   * Phase 2 feedback signature. Fire-and-forget — the parent hook stages
   * the dismissal into its undo window, hides the alert immediately, and
   * commits to the server after the window expires. No double-click guard
   * needed here because the alert disappears synchronously.
   */
  onFeedback?: (
    alertId: string,
    kind: "solved" | "removed",
    note?: string,
  ) => void;
  /** Notes the user typed for an alert they dismissed-then-undid. The chevron
   *  panel pre-populates from this so they don't have to retype. */
  restoredNotes?: Map<string, { kind: "solved" | "removed"; note: string }>;
  /** Deal list used by the "+ CRM TASK" dialog's linked-deal picker. */
  deals?: DealSummary[];
  /** Open the "create CRM task" dialog and write to Zoho on confirm. */
  onCreateCrmTask?: (input: CrmTaskCreateInput) => Promise<void>;
  /** Create a personal task — opens the AddToMyListDialog first. */
  onAddToMyList?: (input: PersonalTaskInput) => Promise<void>;
  /** Save a note to a Zoho Deal — opens the SaveNoteDialog first. */
  onSaveNote?: (input: SaveNoteInput) => Promise<void>;
}

export function AlertsRail({
  alerts,
  loading,
  variant = "sidepanel",
  onRefresh,
  onFeedback,
  restoredNotes,
  deals,
  onCreateCrmTask,
  onAddToMyList,
  onSaveNote,
}: AlertsRailProps) {
  // Group alerts into the three merged buckets, preserving input order.
  const buckets = new Map<MergedSectionKey, Alert[]>();
  for (const merged of MERGED_SECTIONS) {
    buckets.set(merged.key, []);
  }
  for (const a of alerts) {
    const merged = MERGED_SECTIONS.find((m) => m.includes.includes(a.section));
    if (merged) buckets.get(merged.key)!.push(a);
  }

  const [createFor, setCreateFor] = useState<Alert | null>(null);
  const [noteFor, setNoteFor] = useState<Alert | null>(null);
  const [myListFor, setMyListFor] = useState<Alert | null>(null);
  // One alert expanded at a time (v3.1 rule). Click toggles; click another collapses previous.
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  // ---- Phase 2 feedback state ---------------------------------------------
  // Only one note panel can be open across the entire rail. Opening another
  // (different alert OR different kind on same alert) closes the prior one.
  // No submitting-lock here — the parent hook hides the alert from view
  // synchronously on dismiss, so a second click never reaches the same row.
  const [feedbackOpen, setFeedbackOpen] = useState<{
    id: string;
    kind: "solved" | "removed";
    text: string;
  } | null>(null);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2200);
    return () => clearTimeout(t);
  }, [toast]);

  const handleQuickDismiss = (id: string, kind: "solved" | "removed") => {
    // If a note panel is open for this alert, dismiss closes it without
    // sending the partially-typed note. Quick dismiss = no note, by definition.
    if (feedbackOpen?.id === id) setFeedbackOpen(null);
    onFeedback?.(id, kind);
  };

  const handleChevronClick = (id: string, kind: "solved" | "removed") => {
    setFeedbackOpen((cur) => {
      // Toggle: clicking the same chevron again closes the panel.
      if (cur && cur.id === id && cur.kind === kind) return null;
      // Pre-populate from restoredNotes if the user previously dismissed
      // this alert with a note and then undid it.
      const prior = restoredNotes?.get(id);
      const initialText = prior && prior.kind === kind ? prior.note : "";
      return { id, kind, text: initialText };
    });
  };

  const handleNoteTextChange = (text: string) => {
    setFeedbackOpen((cur) => (cur ? { ...cur, text } : null));
  };

  const handleSubmitNote = () => {
    if (!feedbackOpen) return;
    const { id, kind, text } = feedbackOpen;
    setFeedbackOpen(null);
    onFeedback?.(id, kind, text);
  };

  const handleCancelNote = () => setFeedbackOpen(null);

  // Refresh spinner state is driven by the parent's `loading` prop, which is
  // tied to watchtowerLoading in useCockpitData. Local state would flip
  // synchronously because `reload()` is fire-and-forget (returns void), so
  // setRefreshing(true) and setRefreshing(false) would batch in the same render.

  const body = (
    <>
      {loading && alerts.length === 0 && (
        <div className="watchtower-loading">
          <span className="chat-thinking-dot" />
          <span className="chat-thinking-dot" />
          <span className="chat-thinking-dot" />
          <span className="chat-thinking-label">ANALYZING PIPELINE</span>
        </div>
      )}
      {!loading && alerts.length === 0 && (
        <div className="watchtower-empty">No flagged items.</div>
      )}
      {MERGED_SECTIONS.map((merged) => {
        const list = buckets.get(merged.key)!;
        if (list.length === 0) return null;
        return (
          <div className="alert-section" key={merged.key}>
            <div className="alert-section-title">
              {merged.label} · {list.length}
            </div>
            {list.map((a) => (
              <AlertItem
                key={a.id}
                alert={a}
                expanded={expandedId === a.id}
                noteOpenKind={
                  feedbackOpen?.id === a.id ? feedbackOpen.kind : null
                }
                noteText={feedbackOpen?.id === a.id ? feedbackOpen.text : ""}
                hasFeedback={Boolean(onFeedback)}
                onToggle={() =>
                  setExpandedId((cur) => (cur === a.id ? null : a.id))
                }
                onQuickDismiss={handleQuickDismiss}
                onChevronClick={handleChevronClick}
                onNoteTextChange={handleNoteTextChange}
                onSubmitNote={handleSubmitNote}
                onCancelNote={handleCancelNote}
                onCreateCrmClick={
                  onCreateCrmTask ? () => setCreateFor(a) : undefined
                }
                onAddToMyListClick={
                  onAddToMyList ? () => setMyListFor(a) : undefined
                }
                onSaveNoteClick={
                  onSaveNote ? () => setNoteFor(a) : undefined
                }
              />
            ))}
          </div>
        );
      })}
    </>
  );

  return (
    <>
      {variant === "centerpiece" ? (
        <div className="watchtower-center">
          <div className="watchtower-header">
            <div className="watchtower-title">
              <span className="watchtower-title-marker" /> EDGE WATCHTOWER
            </div>
            <div className="watchtower-controls">
              <button
                className="watch-filter-pill"
                disabled
                title="Filter coming soon"
              >
                ALL DEALS
              </button>
              {onRefresh && (
                <button
                  className="watch-filter-pill refresh"
                  onClick={onRefresh}
                  disabled={loading}
                  title="Re-scan watchtower"
                >
                  {loading ? "↻ SCANNING…" : "↻ REFRESH"}
                </button>
              )}
            </div>
          </div>
          <div className="watchtower-body">{body}</div>
        </div>
      ) : variant === "embedded" ? (
        <div className="watchtower-body wt-embedded-body">{body}</div>
      ) : (
        <Panel
          className="alerts-rail"
          title="EDGE WATCHTOWER"
          count={loading ? "SCANNING…" : `${alerts.length} FLAGGED`}
        >
          {body}
        </Panel>
      )}

      {createFor && onCreateCrmTask && (
        <CrmTaskCreateDialog
          initial={{
            subject: createFor.title,
            description: createFor.context,
            deal_id:
              createFor.dealId ??
              detectDealFromAlert(
                `${createFor.title} ${createFor.context}`,
                deals ?? [],
              ),
          }}
          deals={deals ?? []}
          onCreate={async (input) => {
            await onCreateCrmTask(input);
            setCreateFor(null);
            setToast("✓ Created in Zoho");
          }}
          onCancel={() => setCreateFor(null)}
        />
      )}

      {noteFor && onSaveNote && (
        <SaveNoteDialog
          initial={{
            content: `${noteFor.title}\n\n${noteFor.context}`,
            deal_id:
              noteFor.dealId ??
              detectDealFromAlert(
                `${noteFor.title} ${noteFor.context}`,
                deals ?? [],
              ),
          }}
          deals={deals ?? []}
          onSave={async (input) => {
            await onSaveNote(input);
            const dealName =
              deals?.find((d) => d.id === input.deal_id)?.name ?? "deal";
            setNoteFor(null);
            setToast(`✓ Note saved to ${dealName}`);
          }}
          onCancel={() => setNoteFor(null)}
        />
      )}

      {myListFor && onAddToMyList && (
        <AddToMyListDialog
          initial={{
            subject: myListFor.title,
            description: myListFor.context,
          }}
          onSave={async (input) => {
            await onAddToMyList(input);
            setMyListFor(null);
            setToast("✓ Added to MY LIST");
          }}
          onCancel={() => setMyListFor(null)}
        />
      )}

      {toast && <div className="edge-toast">{toast}</div>}
    </>
  );
}

function AlertItem({
  alert,
  expanded,
  noteOpenKind,
  noteText,
  hasFeedback,
  onToggle,
  onQuickDismiss,
  onChevronClick,
  onNoteTextChange,
  onSubmitNote,
  onCancelNote,
  onCreateCrmClick,
  onAddToMyListClick,
  onSaveNoteClick,
}: {
  alert: Alert;
  expanded: boolean;
  /** Which feedback panel is open on this alert (null = no panel). */
  noteOpenKind: "solved" | "removed" | null;
  /** Live value of the note input, owned by the parent rail. */
  noteText: string;
  /** Whether the parent supplied a feedback handler — gates the buttons. */
  hasFeedback: boolean;
  onToggle: () => void;
  onQuickDismiss: (id: string, kind: "solved" | "removed") => void;
  onChevronClick: (id: string, kind: "solved" | "removed") => void;
  onNoteTextChange: (text: string) => void;
  onSubmitNote: () => void;
  onCancelNote: () => void;
  onCreateCrmClick?: () => void;
  onAddToMyListClick?: () => void;
  onSaveNoteClick?: () => void;
}) {
  // Strategic alerts are observation-only: no other actions, only the
  // SOLVED / REMOVE feedback pair (in both collapsed and expanded rows).
  const actionable =
    alert.section !== "strategic" &&
    Boolean(alert.suggestedAction && alert.suggestedAction.trim());

  // Mini buttons must not bubble up — clicking + (CRM) should NOT toggle expand.
  const stop = (handler?: () => void) => (e: React.MouseEvent) => {
    e.stopPropagation();
    handler?.();
  };

  // Render the split SOLVED / REMOVE pair. Used in two places (collapsed row
  // and expanded actions). Same handler set; differs only by CSS scope class.
  const renderDismissPair = (scope: "row" | "detail") =>
    hasFeedback ? (
      <div
        className={clsx("alert-dismiss-cluster", `scope-${scope}`)}
        // Defensive: clicks inside this cluster must not toggle the row.
        onClick={(e) => e.stopPropagation()}
      >
        <SplitButton
          variant="solved"
          label="SOLVED"
          chevronOpen={noteOpenKind === "solved"}
          onMain={() => onQuickDismiss(alert.id, "solved")}
          onChevron={() => onChevronClick(alert.id, "solved")}
        />
        <SplitButton
          variant="removed"
          label="REMOVE"
          chevronOpen={noteOpenKind === "removed"}
          onMain={() => onQuickDismiss(alert.id, "removed")}
          onChevron={() => onChevronClick(alert.id, "removed")}
        />
      </div>
    ) : null;

  return (
    <div
      className={clsx(
        "alert-item",
        alert.severity === "critical" && "critical",
        expanded && "expanded",
      )}
    >
      {/* Collapsed (always-visible) row. Clicking this row toggles expand;
          mini action buttons stop propagation so they're isolated. The
          chevron has no handler — its click bubbles up and toggles. */}
      <div className="alert-row" onClick={onToggle}>
        <div className="alert-row-main">
          <div className="alert-row-title">{alert.title}</div>
          <div className="alert-row-meta">
            {alert.severity === "critical" && (
              <span className="alert-row-critical">▸ CRITICAL</span>
            )}
            {alert.dealName && (
              <span className="alert-row-deal">{alert.dealName}</span>
            )}
            {alert.ageLabel && (
              <span className="alert-row-age">· {alert.ageLabel}</span>
            )}
          </div>
        </div>
        <div className="alert-row-actions">
          {actionable && onCreateCrmClick && (
            <button
              className="alert-mini-btn crm"
              onClick={stop(onCreateCrmClick)}
              title="Create CRM task"
            >
              +
            </button>
          )}
          {actionable && onAddToMyListClick && (
            <button
              className="alert-mini-btn my-list"
              onClick={stop(onAddToMyListClick)}
              title="Add to My List"
            >
              ★
            </button>
          )}
          {actionable && onSaveNoteClick && (
            <button
              className="alert-mini-btn note"
              onClick={stop(onSaveNoteClick)}
              title="Save note"
            >
              ✎
            </button>
          )}
          {renderDismissPair("row")}
          <span className="alert-expand-icon">▸</span>
        </div>
      </div>

      {/* Inline feedback panel — single fixed location per alert (between row
          and detail). Triggered from either the collapsed row's chevron or
          the expanded view's chevron; rendered here in both cases for
          consistency. */}
      {noteOpenKind && (
        <FeedbackPanel
          kind={noteOpenKind}
          value={noteText}
          onChange={onNoteTextChange}
          onSubmit={onSubmitNote}
          onCancel={onCancelNote}
        />
      )}

      {/* Expanded detail. NO onClick — text selection works, and labeled
          action buttons fire their own handlers without stopPropagation
          since they're not inside .alert-row. */}
      <div className="alert-detail">
        <div className="alert-detail-inner">
          <div className="alert-context">{alert.context}</div>
          <div className="alert-detail-actions">
            {actionable && onCreateCrmClick && (
              <button className="alert-action-btn crm" onClick={onCreateCrmClick}>
                + CRM TASK
              </button>
            )}
            {actionable && onAddToMyListClick && (
              <button
                className="alert-action-btn my-list"
                onClick={onAddToMyListClick}
              >
                + MY LIST
              </button>
            )}
            {actionable && onSaveNoteClick && (
              <button className="alert-action-btn note" onClick={onSaveNoteClick}>
                + SAVE NOTE
              </button>
            )}
            {renderDismissPair("detail")}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Split button with a primary action area (instant dismiss) and a chevron
 * sub-area that opens the inline note panel. Two adjacent buttons sharing
 * a visual outline via CSS — kept as separate buttons for accessibility
 * (each has its own label and click target).
 */
function SplitButton({
  variant,
  label,
  chevronOpen,
  onMain,
  onChevron,
}: {
  variant: "solved" | "removed";
  label: string;
  chevronOpen: boolean;
  onMain: () => void;
  onChevron: () => void;
}) {
  return (
    <span className={clsx("alert-split", variant, chevronOpen && "chevron-open")}>
      <button
        type="button"
        className="alert-split-main"
        onClick={onMain}
        title={
          variant === "solved"
            ? "Mark solved (click ▾ to add a note)"
            : "Remove — not relevant (click ▾ to add a note)"
        }
        aria-label={variant === "solved" ? "Mark solved" : "Remove"}
      >
        {label}
      </button>
      <button
        type="button"
        className="alert-split-chevron"
        onClick={onChevron}
        title={
          variant === "solved"
            ? "Mark solved with a note"
            : "Remove with a note"
        }
        aria-label={
          variant === "solved"
            ? "Mark solved with a note"
            : "Remove with a note"
        }
        aria-expanded={chevronOpen}
      >
        ▾
      </button>
    </span>
  );
}

/**
 * Inline note panel that surfaces when the user clicks a SOLVED ▾ or
 * REMOVE ▾ chevron. Submit fires the same dismiss path as the quick
 * variant but with the typed note attached. Enter submits, Esc cancels.
 */
function FeedbackPanel({
  kind,
  value,
  onChange,
  onSubmit,
  onCancel,
}: {
  kind: "solved" | "removed";
  value: string;
  onChange: (text: string) => void;
  onSubmit: () => void;
  onCancel: () => void;
}) {
  // Autofocus the input when the panel opens. Ref-based instead of HTML
  // autoFocus so it works even if the panel re-mounts at the same DOM
  // position (kind switches).
  const inputRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    inputRef.current?.focus();
  }, [kind]);

  return (
    <div
      className={clsx("alert-feedback-panel", kind)}
      // The panel sits inside .alert-item but outside .alert-row; clicks
      // inside still bubble through alert-item, which has no onClick. Be
      // explicit anyway so future refactors don't accidentally re-trigger
      // expand-on-click here.
      onClick={(e) => e.stopPropagation()}
    >
      <span className="alert-feedback-label">
        {kind === "solved" ? "WHY SOLVED?" : "WHY REMOVE?"}
      </span>
      <input
        ref={inputRef}
        type="text"
        className="alert-feedback-input"
        placeholder="Why? (optional)"
        value={value}
        maxLength={500}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            onSubmit();
          } else if (e.key === "Escape") {
            e.preventDefault();
            onCancel();
          }
        }}
      />
      <button
        type="button"
        className="alert-feedback-submit"
        onClick={onSubmit}
      >
        SUBMIT
      </button>
      <button
        type="button"
        className="alert-feedback-cancel"
        onClick={onCancel}
        aria-label="Cancel"
        title="Cancel"
      >
        ✕
      </button>
    </div>
  );
}
